import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import AdvertisersService from "../../../services/OrderingServices/AdvertisersService";
import AdvertiserStatsFilterCard from "../component/AdvertiserStatsFilterCard";
import ExportToExcel from "../../../Components/Common/ExportToExcel";
import { formatDateTime } from "../../../Components/Common/FormatDate";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const AdvertiserStats = () => {
  const [mediaTypeStats, setMediaTypeStats] = useState([]);
  const [screenWiseStats, setScreenWiseStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("media-type");
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    advertiserId: "",
    advertiserName: "",
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    endDate: new Date(),
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    const oneMonthAgo = new Date(
      new Date().setMonth(new Date().getMonth() - 1),
    );
    setFilters({
      advertiserId: "",
      advertiserName: "",
      startDate: oneMonthAgo,
      endDate: new Date(),
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    fetchAdvertiserStats();
  }, [debouncedFilters]);

  async function fetchAdvertiserStats(currentFilters = debouncedFilters) {
    try {
      setLoading(true);

      // Format dates for API - handle both Date objects and strings
      let startDate = currentFilters.startDate;
      let endDate = currentFilters.endDate;

      if (startDate instanceof Date) {
        startDate = startDate.toISOString();
      }
      if (endDate instanceof Date) {
        endDate = endDate.toISOString();
      }

      // Fetch both media type and screen wise stats
      const [mediaTypeResponse, screenWiseResponse] = await Promise.all([
        AdvertisersService.getAdvertiserStatsByMediaType({
          advertiserId: currentFilters.advertiserId || undefined,
          advertiserName: currentFilters.advertiserName || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }),
        AdvertisersService.getAdvertiserStatsByScreenType({
          advertiserId: currentFilters.advertiserId || undefined,
          advertiserName: currentFilters.advertiserName || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }),
      ]);

      // Handle media type response
      const mediaTypeData = Array.isArray(mediaTypeResponse)
        ? mediaTypeResponse
        : mediaTypeResponse?.data || [];
      const mappedMediaType = mediaTypeData.map((item, index) => ({
        id: `media-${item.advertiserId}-${item.mediaType}-${index}`,
        ...item,
      }));
      setMediaTypeStats(mappedMediaType);

      // Handle screen wise response
      const screenWiseData = Array.isArray(screenWiseResponse)
        ? screenWiseResponse
        : screenWiseResponse?.data || [];
      const mappedScreenWise = screenWiseData.map((item, index) => ({
        id: `screen-${item.advertiserId}-${item.screenType}-${index}`,
        ...item,
      }));
      setScreenWiseStats(mappedScreenWise);
    } catch (error) {
      toast.error("Failed to fetch advertiser statistics");
      console.error(error);
      setMediaTypeStats([]);
      setScreenWiseStats([]);
    } finally {
      setLoading(false);
    }
  }

  const mediaTypeColumns = [
    {
      name: "Advertiser Name",
      selector: (row) => row.advertiserName,
      sortable: true,
      grow: 1.2,
    },
    {
      name: "Media Type",
      selector: (row) => (
        <Badge color={row.mediaType === "Video" ? "primary" : "info"}>
          {row.mediaType}
        </Badge>
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Total Impressions",
      selector: (row) => row.totalImpressions || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Total Visible Seconds",
      selector: (row) => row.totalVisibleSeconds || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Total Played Seconds",
      selector: (row) => row.totalPlayedSeconds || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Completed Plays",
      selector: (row) => row.completedPlays || 0,
      sortable: true,
      center: true,
    },
    // {
    //   name: "Unique Devices",
    //   selector: (row) => row.uniqueDevices || 0,
    //   sortable: true,
    //   center: true,
    // },
  ];

  const screenWiseColumns = [
    {
      name: "Advertiser Name",
      selector: (row) => row.advertiserName,
      sortable: true,
      grow: 1.2,
    },
    {
      name: "Screen Type",
      selector: (row) => <Badge color="success">{row.screenType}</Badge>,
      sortable: true,
      center: true,
    },
    {
      name: "Total Impressions",
      selector: (row) => row.totalImpressions || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Total Visible Seconds",
      selector: (row) => row.totalVisibleSeconds || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Total Played Seconds",
      selector: (row) => row.totalPlayedSeconds || 0,
      sortable: true,
      center: true,
    },
    {
      name: "Completed Plays",
      selector: (row) => row.completedPlays || 0,
      sortable: true,
      center: true,
    },
  ];

  // Transform media type stats data for Excel export
  const transformMediaTypeForExport = (data) => {
    return data.map((row, index) => ({
      "SN": index + 1,
      "Advertiser Name": row.advertiserName || "-",
      "Media Type": row.mediaType || "-",
      "Total Impressions": row.totalImpressions || 0,
      "Total Visible Seconds": row.totalVisibleSeconds || 0,
      "Total Played Seconds": row.totalPlayedSeconds || 0,
      "Completed Plays": row.completedPlays || 0,
    }));
  };

  // Transform screen wise stats data for Excel export
  const transformScreenWiseForExport = (data) => {
    return data.map((row, index) => ({
      "SN": index + 1,
      "Advertiser Name": row.advertiserName || "-",
      "Screen Type": row.screenType || "-",
      "Total Impressions": row.totalImpressions || 0,
      "Total Visible Seconds": row.totalVisibleSeconds || 0,
      "Total Played Seconds": row.totalPlayedSeconds || 0,
      "Completed Plays": row.completedPlays || 0,
    }));
  };

  // Fetch function for media type export
  const fetchMediaTypeForExport = async (params) => {
    let startDate = params.startDate;
    let endDate = params.endDate;

    if (startDate instanceof Date) {
      startDate = startDate.toISOString();
    }
    if (endDate instanceof Date) {
      endDate = endDate.toISOString();
    }

    const response = await AdvertisersService.getAdvertiserStatsByMediaType({
      advertiserId: params.advertiserId || undefined,
      advertiserName: params.advertiserName || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });

    // Return in the format expected by ExportToExcel
    const data = Array.isArray(response) ? response : response?.data || [];
    return { data, totalRecords: data.length };
  };

  // Fetch function for screen wise export
  const fetchScreenWiseForExport = async (params) => {
    let startDate = params.startDate;
    let endDate = params.endDate;

    if (startDate instanceof Date) {
      startDate = startDate.toISOString();
    }
    if (endDate instanceof Date) {
      endDate = endDate.toISOString();
    }

    const response = await AdvertisersService.getAdvertiserStatsByScreenType({
      advertiserId: params.advertiserId || undefined,
      advertiserName: params.advertiserName || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });

    // Return in the format expected by ExportToExcel
    const data = Array.isArray(response) ? response : response?.data || [];
    return { data, totalRecords: data.length };
  };

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Advertiser Stats" pageTitle="Advertisers" />
        </div>
        {/* <BreadCrumb pageTitle="Advertiser Statistics" /> */}

        <Container fluid>
          <AdvertiserStatsFilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
          />

          <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
              <div className="d-flex justify-content-between align-items-center border-bottom">
                <Nav tabs className="border-0">
                  <NavItem>
                    <NavLink
                      className={activeTab === "media-type" ? "active" : ""}
                      onClick={() => setActiveTab("media-type")}
                      style={{ cursor: "pointer" }}
                    >
                      Media Type Stats
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "screen-wise" ? "active" : ""}
                      onClick={() => setActiveTab("screen-wise")}
                      style={{ cursor: "pointer" }}
                    >
                      Screen Wise Stats
                    </NavLink>
                  </NavItem>
                </Nav>
                <div className="me-3">
                  <ExportToExcel
                    fetchDataFunction={
                      activeTab === "media-type"
                        ? fetchMediaTypeForExport
                        : fetchScreenWiseForExport
                    }
                    filters={debouncedFilters}
                    fileName={
                      activeTab === "media-type"
                        ? "Advertiser_Stats_Media_Type"
                        : "Advertiser_Stats_Screen_Wise"
                    }
                    dataTransformer={
                      activeTab === "media-type"
                        ? transformMediaTypeForExport
                        : transformScreenWiseForExport
                    }
                    buttonText="Export"
                    buttonSize="sm"
                    buttonColor="success"
                  />
                </div>
              </div>

              <TabContent activeTab={activeTab} className="p-4">
                <TabPane tabId="media-type">
                  {loading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "400px" }}
                    >
                      <Triangle
                        height="80"
                        width="80"
                        color="#4435a0"
                        ariaLabel="loading"
                      />
                    </div>
                  ) : (
                    <CustomDataTable
                      columns={mediaTypeColumns}
                      data={mediaTypeStats}
                      customStyles={customStyles}
                      noDataComponent="No advertiser statistics found"
                    />
                  )}
                </TabPane>

                <TabPane tabId="screen-wise">
                  {/* <Card className="border-0 shadow-sm">
                    <CardBody> */}
                      {loading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ height: "400px" }}
                        >
                          <Triangle
                            height="80"
                            width="80"
                            color="#4435a0"
                            ariaLabel="loading"
                          />
                        </div>
                      ) : (
                        // <CustomDataTable
                        //   columns={screenWiseColumns}
                        //   data={screenWiseStats}
                        //   customStyles={customStyles}
                        //   noDataComponent="No advertiser statistics found"
                        // />

                        <CustomDataTable
                          responsive
                          striped
                          pagination
                          fixedHeader
                          // persistTableHead
                          paginationServer
                          paginationTotalRows={totalRows}
                          onChangePage={(page) => setCurrentPage(page)}
                          onChangeRowsPerPage={(newSize, page) => {
                            setPageSize(newSize);
                            setCurrentPage(1);
                          }}
                          progressPending={loading}
                          customStyles={customStyles}
                          progressComponent={
                            <div className="my-3 text-center">
                              <Triangle
                                visible={true}
                                height="80"
                                width="80"
                                color="#435ebe"
                                ariaLabel="triangle-loading"
                              />
                              <h5 className="mt-2">Loading...</h5>
                            </div>
                          }
                          columns={screenWiseColumns}
                          data={screenWiseStats}
                          noDataComponent={
                            <div className="py-4 text-center">
                              <p className="text-muted">No campaigns found</p>
                            </div>
                          }
                        />
                      )}
                    {/* </CardBody>
                  </Card> */}
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default AdvertiserStats;
